/**
 * Import Service API - Dashboard, Jobs, Configurations, Middleware Status
 */
import api, { type PaginatedResponse } from './client';

// ============================================================================
// Types
// ============================================================================

export interface ImportJob {
  id: number;
  job_id: string;
  correlation_id: string;
  data_type: string;
  data_type_display: string;
  source: string;
  source_display: string;
  is_incremental: boolean;
  supplier: number;
  supplier_name: string;
  status: string;
  status_display: string;
  total_records: number;
  records_created: number;
  records_updated: number;
  records_skipped: number;
  records_failed: number;
  records_unchanged: number;
  records_processed: number;
  started_at: string | null;
  completed_at: string | null;
  duration_seconds: number | null;
  error_message: string;
  created_at: string;
  updated_at: string;
}

export interface HealthCheckDescriptor {
  id: string;
  name: string;
  description: string;
}

export type HealthCheckStatus = 'pass' | 'warning' | 'fail' | 'error';

export interface HealthCheckResponse {
  check_id: string;
  supplier_id: number;
  status: HealthCheckStatus;
  summary: string;
  metrics: Record<string, unknown>;
  samples: Array<Record<string, unknown>>;
  duration_ms: number;
}

export interface ImportJobDetail extends ImportJob {
  error_traceback: string;
  source_file_name: string;
  celery_task_id: string;
  error_breakdown: Array<{ error_code: string; count: number }>;
  debug_sample_raw: Array<Record<string, unknown>>;
  debug_sample_processed: Array<Record<string, unknown>>;
  /** Full raw incoming records for this frame (retained for last 2 imports/site).
   *  Each record carries '_outcome' (created/updated/unchanged/skipped/failed). */
  raw_frame?: Array<Record<string, unknown>> | null;
  raw_frame_retained?: boolean;
}

export interface ImportLog {
  id: number;
  action: string;
  action_display: string;
  entity_type: string;
  entity_id: number | null;
  entity_reference: string;
  source_row: number | null;
  source_key: string;
  fields_changed: Record<string, unknown>;
  error_code: string;
  error_message: string;
  created_at: string;
}

export interface ImportConfiguration {
  id: number;
  supplier: number;
  supplier_name: string;
  data_type: string;
  data_type_display: string;
  schedule_type: string;
  schedule_type_display: string;
  cron_expression: string;
  interval_minutes: number | null;
  is_incremental: boolean;
  batch_size: number;
  max_retries: number;
  retry_delay_seconds: number;
  source_config: Record<string, unknown>;
  field_mappings: Record<string, unknown>;
  auto_create_dependencies: boolean;
  validate_before_import: boolean;
  is_active: boolean;
  last_run_at: string | null;
  last_success_at: string | null;
  consecutive_failures: number;
  created_at: string;
  updated_at: string;
}

export interface DashboardSummary {
  total_jobs: number;
  jobs_today: number;
  jobs_running: number;
  jobs_failed_today: number;
  records_created_today: number;
  records_updated_today: number;
  records_skipped_today: number;
  records_failed_today: number;
  by_data_type: Array<{
    data_type: string;
    total: number;
    succeeded: number;
    failed: number;
    records_created: number;
    records_failed: number;
  }>;
  recent_failures: ImportJob[];
}

export interface MiddlewareQueue {
  queue_key: string;
  depth: number;
  supplier: string;
  supplier_id: number;
  data_type: string;
  configuration_id: number;
}

export interface MiddlewareStatusResponse {
  queues: MiddlewareQueue[];
  error?: string;
}

/** Import source for switching between Poeton and Data Import views. */
export type ImportSource = 'poeton' | 'data_import';

export interface ImportSourceOption {
  key: string;
  source: ImportSource;
  supplier_id: number;
  supplier_name: string;
}

export interface ImportJobFilters {
  supplier?: number;
  data_type?: string;
  source?: string;
  status?: string;
  created_after?: string;
  created_before?: string;
  date_from?: string;
  date_to?: string;
  has_failures?: boolean;
  search?: string;
  page?: number;
  page_size?: number;
  ordering?: string;
}

// ============================================================================
// API
// ============================================================================

function jobsPrefix(source: ImportSource): string {
  switch (source) {
    case 'data_import': return '/imports/data-import-jobs/';
    case 'poeton':
    default:
      return '/imports/poeton-jobs/';
  }
}

export const importsAPI = {
  getSources: () =>
    api.get<ImportSourceOption[]>('/imports/sources/').then(r => r.data),

  /** Raw ERP payload for an order — includes unchanged/skipped rows that
   *  never get an import-log entry. Only the last couple of pushes are
   *  retained, so this answers "what did we just receive". */
  getRawRecord: (params: RawRecordLookupParams) =>
    api.get<RawRecordLookup>('/imports/raw-record/', { params }).then(r => r.data),

  /** What the orphan-cancellation logic currently sees. Read-only. */
  getOrphanShadow: (params?: { site?: number; missed?: number }) =>
    api.get<OrphanShadowReport>('/imports/orphan-shadow/', { params }).then(r => r.data),

  /** Cancel reviewed orphan lines. Same semantics as the automatic sweep. */
  cancelOrphans: (lineIds: number[], reason?: string) =>
    api.post<{ cancelled: number; closed_headers: number; skipped: number }>(
      '/imports/orphan-cancel/', { line_ids: lineIds, ...(reason ? { reason } : {}) },
    ).then(r => r.data),

  getDashboard: (source: ImportSource = 'poeton', supplierId?: number) =>
    api.get<DashboardSummary>('/imports/dashboard/', {
      params: supplierId ? { source, supplier: supplierId } : { source },
    }).then(r => r.data),

  getJobs: (filters?: ImportJobFilters, source: ImportSource = 'poeton') => {
    const prefix = jobsPrefix(source);
    return api.get<PaginatedResponse<ImportJob>>(prefix, { params: filters }).then(r => r.data);
  },

  getJob: (jobId: string, source: ImportSource = 'poeton') => {
    const prefix = jobsPrefix(source);
    return api.get<ImportJobDetail>(`${prefix}${jobId}/`).then(r => r.data);
  },

  getJobLogs: (
    jobId: string,
    params?: { action?: string; entity_type?: string; error_code?: string; page?: number },
    source: ImportSource = 'poeton',
  ) => {
    const prefix = jobsPrefix(source);
    return api.get<PaginatedResponse<ImportLog>>(`${prefix}${jobId}/logs/`, { params }).then(r => r.data);
  },

  triggerImport: (data: { data_type: string; supplier_id: number; configuration_id?: number }) =>
    api.post<ImportJob>('/imports/jobs/', data).then(r => r.data),

  getConfigurations: (filters?: { supplier?: number; data_type?: string; is_active?: boolean }) =>
    api.get<PaginatedResponse<ImportConfiguration>>('/imports/configurations/', { params: filters }).then(r => r.data),

  getMiddlewareStatus: (source: ImportSource = 'poeton') =>
    api.get<MiddlewareStatusResponse>('/imports/middleware-status/', { params: { source } }).then(r => r.data),

  listHealthChecks: () =>
    api.get<{ checks: HealthCheckDescriptor[] }>('/imports/health-checks/').then(r => r.data.checks),

  runHealthCheck: (checkId: string, supplierId: number) =>
    api.post<HealthCheckResponse>(
      `/imports/health-checks/${checkId}/run/`,
      { supplier_id: supplierId },
    ).then(r => r.data),

  cancelJob: (jobId: string) =>
    api.post<{ message: string; status: string }>(`/imports/jobs/${jobId}/cancel/`).then(r => r.data),
};

// ---------------------------------------------------------------------------
// Raw ERP payload lookup by order number
// ---------------------------------------------------------------------------

/** One raw record as the ERP sent it, with where it arrived. */
export interface RawRecordMatch {
  import_no: number;
  page_index: number;
  page_count: number;
  site: number | null;
  job_status: string;
  received_at: string;
  row_index: number;
  /** created | updated | unchanged | skipped | failed | unknown */
  outcome: string;
  line_no: string;
  record: Record<string, unknown>;
}

export interface RawRecordLookup {
  order_no: string;
  import_type: string;
  found: boolean;
  match_count: number;
  retained_pushes: { import_no: number; received_at: string }[];
  /** Same line arriving twice in one push — last page processed wins. */
  duplicate_lines: { import_no: number; line_no: string }[];
  matches: RawRecordMatch[];
}

export interface RawRecordLookupParams {
  order_no: string;
  site?: number;
  import_type?: string;
}

// ---------------------------------------------------------------------------
// Orphan sweep — shadow report (read-only; the endpoint never writes)
// ---------------------------------------------------------------------------

export interface OrphanCandidate {
  line_id: number;
  po_id: number;
  po_number: string;
  line_number: number;
  part_number: string;
  quantity: string;
  received_qty: string;
  customer: string;
  status: string;
  processing_stage: string;
  last_seen_import_no: number | null;
  first_detected_at?: string;
  age_days: number | null;
}

export interface OrphanSiteReport {
  site_id: number;
  site_name: string;
  complete_pushes: number[];
  threshold_import_no: number | null;
  open_lines: number;
  stamped_lines: number;
  skipped_reason: string;
  would_cancel_count: number;
  would_cancel: OrphanCandidate[];
  never_seen_count: number;
  never_seen: OrphanCandidate[];
}

export interface OrphanShadowReport {
  /** off | shadow | on */
  mode: string;
  missed_pushes: number;
  generated_at: string;
  /** When the hourly sweep last wrote its verdict. */
  last_sweep_at: string | null;
  sites: OrphanSiteReport[];
}
